/**
 * Black Friday 2026 campaign: the band at the top of every screen.
 *
 * The code is `blackfriday2026` and it grants 48 hours in which nothing is charged — no credits, no
 * feature units, no daily cap. The campaign is closed to new redemptions, so the band no longer offers
 * the code: what is left is the countdown of a window already running, and the over-bands after it. The
 * window's end arrives as `freeUsageUntil` on GetCreditsOverview.
 *
 * Everything here is pure, so the states are testable without React — and so web, iOS and Android
 * can be read side by side and checked case for case (`blackFridayBannerState` ·
 * `BlackFriday.bannerState` · `blackFridayBannerState`).
 *
 * ⚠️ WHY THE "OVER" BANNERS NEED A REMEMBERED END TIME. `CreditsOverview.java` reports `freeUsageUntil`
 * only while it is still in the future, so once the window closes the client needs another source for
 * its end. The account-wide `lastFreeUsageUntil` is the main one; this device's remembered end stays
 * underneath it for an older BFF and for the moment between redeeming and the next credits read.
 */

/** The campaign's code. One literal, read by Dev Tools and the parity test. */
export const BLACK_FRIDAY_PROMO_CODE = 'blackfriday2026';

/**
 * When the countdown band switches from "Free unlimited credits" to "Final hours of unlimited credits".
 *
 * Eight hours — the written brief's own number
 * (`artifacts/bfcm-packs/bfcm-in-product-assets-dev-brief.md`), confirmed on 2026-10-17. One constant,
 * shared by the three surfaces, so the copy cannot switch at a different hour on one of them.
 */
export const BLACK_FRIDAY_FINAL_HOURS_MS = 8 * 60 * 60 * 1000;

/**
 * The band's height before it has been measured — the design's one-line desktop bar (56) plus the slot's
 * own 24 top padding. Only the first frame reads it: `AppFrame` MEASURES the real height, because the two
 * over-bands wrap at narrow widths and in a long translation.
 */
export const BLACK_FRIDAY_BANNER_HEIGHT_PX = 80;

/** Retries after a failed wins read, before the band settles on "See plans". Same on all three surfaces. */
export const BLACK_FRIDAY_WINS_RETRIES = 2;

/** What the code grants. Used only to date the window's START from its end, for the wins lookback. */
export const BLACK_FRIDAY_WINDOW_MS = 48 * 60 * 60 * 1000;

/**
 * How long the over-band shows after the window ends, for a reader who never presses its X. Counted
 * from the window's end, which comes from the server's account-wide `lastFreeUsageUntil`, so every
 * device and browser stops on the same day, and a new sign-in does not start it again.
 */
export const BLACK_FRIDAY_ENDED_BAND_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * When the over-band for this remembered end goes away, or null with no usable end. Nothing repaints the
 * band at that moment on its own, so the band schedules one refresh for it.
 */
export function blackFridayEndedBandEndsAt(seenUntilIso: string | null): number | null {
  const ended = seenUntilIso ? new Date(seenUntilIso).getTime() : NaN;
  return Number.isFinite(ended) ? ended + BLACK_FRIDAY_ENDED_BAND_MS : null;
}

/**
 * The bands of the design (Figma `4217:25450`), plus `hidden`.
 *
 * The two `ended-*` bands are ONE band with two destinations, shown only to an account that is NOT on
 * a paid plan (a paying account gets neither — 2026-10-29, a Max account was told its "free hours are
 * over" with an upgrade CTA). Which one a reader gets is decided by the wins and by whether the reader
 * already opened them:
 *
 * | the reader | band | CTA |
 * |---|---|---|
 * | something landed in the window, wins not opened from the band yet | `ended-wins` | See your wins |
 * | opened the wins from the band, or nothing landed | `ended-plans` | See plans → the package picker |
 *
 * The wins band is a step, not an end: once its CTA is pressed the band turns into the plans band and
 * stays there until its X. The X on either over-band ends the campaign on this device.
 */
export type BlackFridayBannerState =
  | 'hidden'
  | 'running'
  | 'final'
  | 'ended-plans'
  | 'ended-wins';

export interface BlackFridayBannerInput {
  /** `specs.symphony.BlackFridayCampaign`. The whole feature is off without it. */
  campaignOn: boolean;
  /**
   * Whether the credits read has SETTLED. Before it does, "no open window" and "we have not looked
   * yet" are the same `null` — and offering 48 free hours to someone whose hours are already running
   * reads as broken. Unknown is not "no".
   */
  accountKnown: boolean;
  /** `freeUsageUntil` from the credits overview: the open window's end, or null when none is open. */
  freeUsageUntilIso: string | null;
  /** The last window end: the account's `lastFreeUsageUntil`, else this device's memory. */
  seenUntilIso: string | null;
  /** The over band's X — persisted, so one close ends it. */
  endedDismissed: boolean;
  /** Whether a win landed inside the window. Picks `ended-wins` over `ended-plans`; see the type. */
  hasWindowWins: boolean;
  /** The wins band's CTA was pressed — persisted. From then on the over-band is `ended-plans`. */
  winsOpened: boolean;
  /**
   * The settled credits read says the account is on a PAID plan (`plan.kind == PAID`). A paying account
   * never gets either over-band: "Your 48 free hours are over. Upgrade to continue" is a free-trial
   * message, and on a Max account (which pays and already holds the plans the CTA sells) it reads as
   * broken. Unknown is `false` — the over-band is only reachable with the read settled, so by then the
   * field is there.
   */
  isPaidPlan: boolean;
  now: number;
}

function activeUntil(iso: string | null, now: number): number | null {
  if (!iso) return null;
  const until = new Date(iso).getTime();
  return Number.isFinite(until) && until > now ? until : null;
}

/**
 * Which band to draw. The one place the states are decided, shared by the banner, the modal gate
 * and the credits-card title.
 */
export function blackFridayBannerState(input: BlackFridayBannerInput): BlackFridayBannerState {
  const { campaignOn, accountKnown, freeUsageUntilIso, seenUntilIso, now } = input;
  if (!campaignOn || !accountKnown) return 'hidden';

  const open = activeUntil(freeUsageUntilIso, now);
  if (open !== null) return open - now <= BLACK_FRIDAY_FINAL_HOURS_MS ? 'final' : 'running';

  // No window open. Either this device watched one close, or it has never seen one at all.
  const ended = seenUntilIso ? new Date(seenUntilIso).getTime() : NaN;
  if (Number.isFinite(ended) && ended <= now) {
    // Hidden for a paying account, whose plan is not something these hours were a trial of (see
    // `isPaidPlan`).
    if (input.endedDismissed || input.isPaidPlan || now - ended >= BLACK_FRIDAY_ENDED_BAND_MS) return 'hidden';
    return input.hasWindowWins && !input.winsOpened ? 'ended-wins' : 'ended-plans';
  }

  // Nothing running and nothing seen: the campaign no longer offers the code, so there is no band.
  return 'hidden';
}

/**
 * The Black Friday band is Symphony's own offer ("See plans"); on the Plans and Domain pages it would compete with buying a
 * Wix plan or domain, two products both called plans on one screen, so it stays off there (2026-10-27).
 */
export function blackFridayBannerHiddenOn(pathname: string): boolean {
  return pathname === '/workspace/[slug]/plan' || pathname === '/workspace/[slug]/domain';
}

/** Whether the band is one of the two counting down — the states whose copy changes every second. */
export function isBlackFridayCountdown(state: BlackFridayBannerState): boolean {
  return state === 'running' || state === 'final';
}

/**
 * `47:57:37` — the design's timer. Hours are NOT wrapped at 24 (the window is 48 long) and every field
 * is two digits, so the band's width does not jitter as the numbers fall. A window already closed reads
 * `00:00:00` rather than going negative.
 */
export function formatBlackFridayCountdown(remainingMs: number): string {
  const total = Math.max(0, Math.floor(remainingMs / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

/**
 * Whether any win landed inside the free window, which is what picks "See your wins" over "See plans".
 *
 * Anchored on the window rather than on a bare "last 48 hours" from now: the over-band can still be on
 * screen days later, and by then "the last 48 hours" is a different and empty stretch of time. The
 * window's start is derived from its end because the hours a code grants are what we know; several
 * grants add up, so this can under-reach on a stacked window, never over-reach.
 */
export function hasWinsInWindow(winCreatedDates: readonly string[], windowEndIso: string | null): boolean {
  if (!windowEndIso) return false;
  const end = new Date(windowEndIso).getTime();
  if (!Number.isFinite(end)) return false;
  const start = end - BLACK_FRIDAY_WINDOW_MS;
  return winCreatedDates.some((iso) => {
    const at = new Date(iso).getTime();
    return Number.isFinite(at) && at >= start && at <= end;
  });
}
